import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import Spinner from './Spinner';

const BADGE_COLORS = {
    Available: ['#d1e7dd', '#0f5132', '#1b4332', '#b7f0c8'],
    LOA: ['#fff3cd', '#664d03', '#5c4a12', '#ffe8a3'],
    'Extended LOA': ['#fff3cd', '#664d03', '#5c4a12', '#ffe8a3'],
    'Approved leave': ['#cfe2ff', '#084298', '#1a3358', '#9ec5fe'],
    Unavailable: ['#f8d7da', '#842029', '#5c1a1f', '#f1aeb5'],
    Absent: ['#f8d7da', '#842029', '#5c1a1f', '#f1aeb5'],
    Training: ['#cff4fc', '#055160', '#164049', '#9eeaf9'],
    TDY: ['#cff4fc', '#055160', '#164049', '#9eeaf9'],
    Unassigned: ['#e2e3e5', '#41464b', '#3a3a3a', '#d0d0d0'],
    'Pending Start': ['#cfe2ff', '#084298', '#1a3358', '#9ec5fe'],
    Inactive: ['#f8d7da', '#842029', '#5c1a1f', '#f1aeb5'],
};

function stylesFor(theme) {
    const dark = theme === 'dark';
    const border = dark ? '#444' : '#d8dee4';
    const card = dark ? '#252525' : '#fff';
    const muted = dark ? '#9aa6b2' : '#66717d';
    return {
        dark,
        border,
        card,
        muted,
        hint: { color: muted, fontSize: '13px', margin: '0 0 14px' },
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginBottom: '16px',
        },
        group: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontSize: '13px', fontWeight: 600 },
        input: {
            padding: '10px',
            borderRadius: '5px',
            border: `1px solid ${border}`,
            backgroundColor: dark ? '#2a2a2a' : '#fff',
            color: dark ? '#e0e0e0' : '#212529',
            minWidth: '150px',
        },
        button: {
            padding: '10px 14px',
            borderRadius: '5px',
            border: 'none',
            backgroundColor: dark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
        },
        secondary: {
            padding: '10px 14px',
            borderRadius: '5px',
            border: `1px solid ${border}`,
            backgroundColor: 'transparent',
            color: dark ? '#e0e0e0' : '#212529',
            cursor: 'pointer',
            fontWeight: 600,
        },
        summary: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '10px',
            marginBottom: '18px',
        },
        summaryCard: {
            padding: '12px',
            border: `1px solid ${border}`,
            borderRadius: '8px',
            backgroundColor: dark ? '#252525' : '#f7f9fb',
            textAlign: 'center',
        },
        summaryNumber: { fontSize: '1.35rem', fontWeight: 700 },
        summaryLabel: { fontSize: '12px', color: muted },
        site: {
            border: `1px solid ${border}`,
            borderRadius: '12px',
            marginBottom: '20px',
            overflow: 'hidden',
            backgroundColor: dark ? '#181818' : '#f7f9fb',
        },
        siteHeader: {
            padding: '14px 18px',
            backgroundColor: dark ? '#202a34' : '#dce9f7',
            borderBottom: `1px solid ${border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
        },
        siteTitle: { margin: 0, fontSize: '20px' },
        workCenters: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
            gap: '16px',
            padding: '16px',
            alignItems: 'start',
        },
        workCenter: {
            border: `1px solid ${border}`,
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: dark ? '#1f1f1f' : '#fff',
        },
        wcHeader: {
            padding: '12px 14px',
            borderBottom: `1px solid ${border}`,
            backgroundColor: dark ? '#2a2a2a' : '#eef2f6',
            cursor: 'pointer',
        },
        wcTitle: { margin: 0, fontSize: '15px', fontWeight: 700 },
        wcMeta: { marginTop: '4px', color: muted, fontSize: '12px' },
        tree: { padding: '14px' },
        nodeWrap: {
            position: 'relative',
            marginLeft: '18px',
            paddingLeft: '20px',
            borderLeft: `2px solid ${dark ? '#46515d' : '#c6d0da'}`,
        },
        nodeBranch: {
            position: 'absolute',
            top: '24px',
            left: 0,
            width: '18px',
            borderTop: `2px solid ${dark ? '#46515d' : '#c6d0da'}`,
        },
        node: {
            border: `1px solid ${border}`,
            borderRadius: '8px',
            padding: '10px',
            marginBottom: '10px',
            backgroundColor: card,
            boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,.06)',
        },
        nodeButton: {
            padding: 0,
            border: 0,
            background: 'none',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            flex: 1,
        },
        nodeTop: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            justifyContent: 'space-between',
        },
        name: { fontWeight: 700, fontSize: '14px' },
        details: { color: muted, fontSize: '12px', lineHeight: 1.45, marginTop: '3px' },
        badge: {
            padding: '2px 7px',
            borderRadius: '999px',
            fontSize: '10px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
        },
        expand: {
            width: '24px',
            height: '24px',
            border: `1px solid ${border}`,
            borderRadius: '50%',
            backgroundColor: dark ? '#333' : '#f5f7f9',
            color: 'inherit',
            cursor: 'pointer',
            fontWeight: 700,
            lineHeight: 1,
        },
        children: { marginTop: '2px' },
        empty: { color: muted, fontSize: '13px', padding: '14px', textAlign: 'center' },
        unassigned: {
            border: `1px dashed ${border}`,
            borderRadius: '10px',
            padding: '14px',
            marginTop: '16px',
        },
    };
}

function badgeStyle(status, dark) {
    const palette = BADGE_COLORS[status] || BADGE_COLORS.Unassigned;
    return {
        backgroundColor: dark ? palette[2] : palette[0],
        color: dark ? palette[3] : palette[1],
    };
}

function filterNodes(nodes, search) {
    if (!search) return nodes;
    return nodes.reduce((result, node) => {
        const children = filterNodes(node.children || [], search);
        const text = [
            node.display_name,
            node.employee_number,
            node.job_title,
            node.position_title,
            node.shift_name,
        ]
            .join(' ')
            .toLowerCase();
        if (text.includes(search) || children.length > 0) {
            result.push({ ...node, children });
        }
        return result;
    }, []);
}

function TreeNode({ node, depth, styles, expanded, toggle, onSelect }) {
    const children = node.children || [];
    const isExpanded = expanded.has(node.id);
    return (
        <div style={depth > 0 ? styles.nodeWrap : { position: 'relative' }}>
            {depth > 0 && <span style={styles.nodeBranch} />}
            <div style={styles.node}>
                <div style={styles.nodeTop}>
                    {children.length > 0 && (
                        <button
                            type="button"
                            style={styles.expand}
                            onClick={() => toggle(node.id)}
                            aria-label={isExpanded ? 'Collapse reports' : 'Expand reports'}
                        >
                            {isExpanded ? '−' : '+'}
                        </button>
                    )}
                    <button
                        type="button"
                        style={styles.nodeButton}
                        onClick={() => onSelect?.(node)}
                    >
                        <div style={styles.name}>{node.display_name}</div>
                        <div style={styles.details}>
                            #{node.employee_number}
                            {(node.position_title || node.job_title)
                                ? ` · ${node.position_title || node.job_title}`
                                : ''}
                            {node.shift_name ? ` · ${node.shift_name}` : ''}
                        </div>
                        {(node.direct_reports > 0 || node.return_date) && (
                            <div style={styles.details}>
                                {node.direct_reports > 0
                                    ? `${node.direct_reports} direct / ${node.total_reports} total reports`
                                    : ''}
                                {node.return_date
                                    ? `${node.direct_reports > 0 ? ' · ' : ''}Return ${node.return_date}`
                                    : ''}
                            </div>
                        )}
                    </button>
                    <span
                        style={{
                            ...styles.badge,
                            ...badgeStyle(node.operational_badge, styles.dark),
                        }}
                    >
                        {node.operational_badge}
                    </span>
                </div>
            </div>
            {children.length > 0 && isExpanded && (
                <div style={styles.children}>
                    {children.map((child) => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            styles={styles}
                            expanded={expanded}
                            toggle={toggle}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ManningFunctionalOrg({ theme, sites, workCenters, onSelectEmployee }) {
    const styles = useMemo(() => stylesFor(theme), [theme]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState(new Set());
    const [expandedSites, setExpandedSites] = useState(new Set());
    const [expandedWcs, setExpandedWcs] = useState(new Set());
    const [filters, setFilters] = useState({
        as_of: new Date().toISOString().slice(0, 10),
        site: '',
        work_center: '',
        search: '',
    });

    const filteredWorkCenters = useMemo(
        () =>
            workCenters.filter(
                (wc) =>
                    wc.is_active &&
                    (!filters.site || String(wc.site) === String(filters.site))
            ),
        [workCenters, filters.site]
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = { as_of: filters.as_of };
            if (filters.site) params.site = filters.site;
            if (filters.work_center) params.work_center = filters.work_center;
            const response = await api.get('/employees/functional-org/', { params });
            setData(response.data);

            const employeeIds = new Set();
            const siteIds = new Set();
            const workCenterIds = new Set();
            const collect = (nodes) => {
                nodes.forEach((node) => {
                    employeeIds.add(node.id);
                    collect(node.children || []);
                });
            };
            response.data.sites.forEach((site) => {
                siteIds.add(site.id);
                site.work_centers.forEach((wc) => {
                    workCenterIds.add(wc.id);
                    collect(wc.roots || []);
                });
            });
            setExpanded(employeeIds);
            setExpandedSites(siteIds);
            setExpandedWcs(workCenterIds);
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                    requestError?.message ||
                    'Failed to load functional organization'
            );
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filters.as_of, filters.site, filters.work_center]);

    useEffect(() => {
        load();
    }, [load]);

    const search = filters.search.trim().toLowerCase();
    const visibleSites = useMemo(() => {
        if (!data) return [];
        return data.sites
            .map((site) => {
                const workCentersVisible = site.work_centers
                    .map((wc) => ({
                        ...wc,
                        roots: filterNodes(wc.roots || [], search),
                    }))
                    .filter(
                        (wc) =>
                            !search ||
                            wc.roots.length > 0 ||
                            `${wc.code} ${wc.name}`.toLowerCase().includes(search)
                    );
                return { ...site, work_centers: workCentersVisible };
            })
            .filter(
                (site) =>
                    !search ||
                    site.work_centers.length > 0 ||
                    site.name.toLowerCase().includes(search)
            );
    }, [data, search]);

    const toggleSet = (setter, id) => {
        setter((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const setAllExpanded = (shouldExpand) => {
        if (!data) return;
        const employees = new Set();
        const siteIds = new Set();
        const wcIds = new Set();
        const collect = (nodes) =>
            nodes.forEach((node) => {
                employees.add(node.id);
                collect(node.children || []);
            });
        data.sites.forEach((site) => {
            siteIds.add(site.id);
            site.work_centers.forEach((wc) => {
                wcIds.add(wc.id);
                collect(wc.roots || []);
            });
        });
        setExpanded(shouldExpand ? employees : new Set());
        setExpandedSites(shouldExpand ? siteIds : new Set());
        setExpandedWcs(shouldExpand ? wcIds : new Set());
    };

    return (
        <div>
            <p style={styles.hint}>
                Functional hierarchy based on each employee’s effective primary assignment
                and assigned supervisor. Click a person to edit their employee record.
            </p>

            <div style={styles.filters}>
                <div style={styles.group}>
                    <label style={styles.label}>As of</label>
                    <input
                        type="date"
                        style={styles.input}
                        value={filters.as_of}
                        onChange={(event) =>
                            setFilters((current) => ({
                                ...current,
                                as_of: event.target.value,
                            }))
                        }
                    />
                </div>
                <div style={styles.group}>
                    <label style={styles.label}>Site</label>
                    <select
                        style={styles.input}
                        value={filters.site}
                        onChange={(event) =>
                            setFilters((current) => ({
                                ...current,
                                site: event.target.value,
                                work_center: '',
                            }))
                        }
                    >
                        <option value="">All sites</option>
                        {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                                {site.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.group}>
                    <label style={styles.label}>Work center</label>
                    <select
                        style={styles.input}
                        value={filters.work_center}
                        onChange={(event) =>
                            setFilters((current) => ({
                                ...current,
                                work_center: event.target.value,
                            }))
                        }
                    >
                        <option value="">All work centers</option>
                        {filteredWorkCenters.map((wc) => (
                            <option key={wc.id} value={wc.id}>
                                {wc.code} — {wc.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.group}>
                    <label style={styles.label}>Search</label>
                    <input
                        style={styles.input}
                        placeholder="Name, employee #, title…"
                        value={filters.search}
                        onChange={(event) =>
                            setFilters((current) => ({
                                ...current,
                                search: event.target.value,
                            }))
                        }
                    />
                </div>
                <button type="button" style={styles.button} onClick={load}>
                    Apply
                </button>
                <button type="button" style={styles.secondary} onClick={() => setAllExpanded(true)}>
                    Expand all
                </button>
                <button type="button" style={styles.secondary} onClick={() => setAllExpanded(false)}>
                    Collapse all
                </button>
            </div>

            {data && (
                <div style={styles.summary}>
                    {[
                        ['Sites', data.totals.sites],
                        ['Work centers', data.totals.work_centers],
                        ['Employees', data.totals.employees],
                        ['Supervisors', data.totals.supervisors],
                        ['Unassigned', data.totals.unassigned],
                    ].map(([label, value]) => (
                        <div key={label} style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{value}</div>
                            <div style={styles.summaryLabel}>{label}</div>
                        </div>
                    ))}
                </div>
            )}

            {loading && <div style={styles.empty}><Spinner /></div>}
            {!loading && error && <div style={styles.empty}>{error}</div>}

            {!loading &&
                !error &&
                visibleSites.map((site) => (
                    <section key={site.id} style={styles.site}>
                        <div
                            style={styles.siteHeader}
                            onClick={() => toggleSet(setExpandedSites, site.id)}
                            role="button"
                            tabIndex={0}
                        >
                            <h3 style={styles.siteTitle}>
                                {expandedSites.has(site.id) ? '−' : '+'} {site.name}
                            </h3>
                            <span>{site.employee_count} employees</span>
                        </div>
                        {expandedSites.has(site.id) && (
                            <div style={styles.workCenters}>
                                {site.work_centers.map((wc) => (
                                    <div key={wc.id} style={styles.workCenter}>
                                        <div
                                            style={styles.wcHeader}
                                            onClick={() => toggleSet(setExpandedWcs, wc.id)}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <h4 style={styles.wcTitle}>
                                                {expandedWcs.has(wc.id) ? '−' : '+'}{' '}
                                                {wc.code} — {wc.name}
                                            </h4>
                                            <div style={styles.wcMeta}>
                                                {wc.employee_count} employees
                                                {wc.supervisor_name
                                                    ? ` · Functional lead: ${wc.supervisor_name}`
                                                    : ' · No functional lead assigned'}
                                            </div>
                                        </div>
                                        {expandedWcs.has(wc.id) && (
                                            <div style={styles.tree}>
                                                {wc.roots.length > 0 ? (
                                                    wc.roots.map((root) => (
                                                        <TreeNode
                                                            key={root.id}
                                                            node={root}
                                                            depth={0}
                                                            styles={styles}
                                                            expanded={expanded}
                                                            toggle={(id) => toggleSet(setExpanded, id)}
                                                            onSelect={onSelectEmployee}
                                                        />
                                                    ))
                                                ) : (
                                                    <div style={styles.empty}>
                                                        No employees in this work center.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                ))}

            {!loading && !error && data?.unassigned?.length > 0 && (
                <section style={styles.unassigned}>
                    <strong>Unassigned employees ({data.unassigned.length})</strong>
                    <div style={styles.details}>
                        Add a current primary assignment to place these employees in the tree.
                    </div>
                    <div style={{ ...styles.workCenters, padding: '12px 0 0' }}>
                        {data.unassigned
                            .filter((employee) => {
                                if (!search) return true;
                                return `${employee.display_name} ${employee.employee_number} ${employee.job_title}`
                                    .toLowerCase()
                                    .includes(search);
                            })
                            .map((employee) => (
                                <button
                                    key={employee.id}
                                    type="button"
                                    style={styles.node}
                                    onClick={() => onSelectEmployee?.(employee)}
                                >
                                    <div style={styles.name}>{employee.display_name}</div>
                                    <div style={styles.details}>
                                        #{employee.employee_number}
                                        {employee.job_title ? ` · ${employee.job_title}` : ''}
                                    </div>
                                </button>
                            ))}
                    </div>
                </section>
            )}

            {!loading && !error && visibleSites.length === 0 && (
                <div style={styles.empty}>No functional organization matches the filters.</div>
            )}
        </div>
    );
}

export default ManningFunctionalOrg;
